"use client";

import { useCallback, useEffect, useState } from "react";
import type { BrowserListing } from "./listing-filters";

const requests = new Map<string, Promise<BrowserListing[]>>();

function isBrowserListingArray(value: unknown): value is BrowserListing[] {
  return (
    Array.isArray(value) &&
    value.every(
      (entry: unknown) =>
        typeof entry === "object" &&
        entry !== null &&
        typeof Reflect.get(entry, "id") === "string" &&
        typeof Reflect.get(entry, "card") === "object",
    )
  );
}

async function fetchListings(href: string): Promise<BrowserListing[]> {
  const response = await fetch(href, { headers: { accept: "application/json" } });
  if (!response.ok) throw new Error(`Listings request failed with ${response.status}`);
  const body: unknown = await response.json();
  if (!isBrowserListingArray(body)) throw new Error("Listings response had an unexpected shape");
  return body;
}

function requestListings(href: string): Promise<BrowserListing[]> {
  const pending = requests.get(href);
  if (pending) return pending;
  const request = fetchListings(href).finally(() => requests.delete(href));
  requests.set(href, request);
  return request;
}

export type LocationListings = {
  all: BrowserListing[] | null;
  failed: boolean;
  request: () => void;
};

export function useLocationListings(href: string | null, needed: boolean): LocationListings {
  const [all, setAll] = useState<BrowserListing[] | null>(null);
  const [failed, setFailed] = useState(false);
  const request = useCallback(() => {
    if (href === null || all !== null) return;
    requestListings(href).then(
      (listings) => {
        setAll(listings);
        setFailed(false);
      },
      () => setFailed(true),
    );
  }, [href, all]);

  useEffect(() => {
    if (needed && all === null) request();
  }, [needed, all, request]);

  return { all, failed, request };
}
